import React from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';

import {
  updateFromPublicSharePage,
  updateIsFromSlackDesktopApp,
  updateIsInlineEmbedOnLoom,
  updateVideoPlayerFromLinkExpand,
  updateVideoPlayerParentLocation,
} from 'actions/videoPlayer';
import { SEND_HLS_PLAYBACK_STATS } from 'constants/featureFlag';
import { TARGET_SHARE_PAGE_EMBED_PARAM } from 'constants/oEmbed';
import { NOT_FOUND } from 'constants/routes';
import { USER, VIDEO } from 'constants/ssr';
import { getFeatureFlagCreator } from 'creators/featureFlag';
import { getUserCreator } from 'creators/user';
import { initVideoCreator } from 'creators/videoPlayer';
import { fetchWorkspaces } from 'creators/workspace';
import sentryActionState from 'middleware/sentry';
import { injectCustomBrandColors } from 'utilities/custom-branding';
import { isSlackDesktop as isFromSlackApp } from 'utilities/device';
import { GraphQLProvider } from 'utilities/graphql';
import * as loggerx from 'utilities/loggerx';
import * as rum from 'utilities/rum';
import * as storeUtil from 'utilities/store';
import { getParam, isFromPublicSharePage } from 'utilities/url';

import { UrlQueryParamContextWrapper } from './contexts/UrlQueryParamContext';

import EmbedVideo from 'components/embed-video';
import { AnalyticsTrackPageWrapper } from 'components/analytics-wrapper';

// dd real user metrics
rum.init();

function initStore(optionalStore = null) {
  if (optionalStore) {
    return storeUtil.init(null, null, optionalStore);
  }

  const middleware = [];
  const ExceptionsTracker = loggerx.initTracker();

  if (ExceptionsTracker) {
    middleware.push(sentryActionState(ExceptionsTracker));
  }

  return storeUtil.init(middleware);
}

const { fromPublicSharePage, parentLocation } = isFromPublicSharePage();

if (!fromPublicSharePage && !window[VIDEO]) {
  window.location.href = NOT_FOUND;
}

// link-expanding params
const fromLinkExpand = getParam('from_link_expand') === 'true';
const hideOwner = getParam('hide_owner') === 'true';
const hideSpeedSelector = getParam('hide_speed') === 'true';
const hideShare = getParam('hide_share') === 'true';
const hideTitle = getParam('hide_title') === 'true';
const hideEmbedTopBar = Boolean(getParam('hideEmbedTopBar'));

const fromUrl = window.decodeURIComponent(getParam('from_url') || '');

// TARGET_SHARE_PAGE_EMBED_PARAM lets us know that this is the main
// video for this share page and should use the parent reducer. Otherwise
// we may have inline expanded loom embeds in comments start to use
// the parent reducer and things might get schwifty
const isMainVideoEmbedOnSharePage =
  fromPublicSharePage && getParam(TARGET_SHARE_PAGE_EMBED_PARAM) === '1';

function init() {
  const store = initStore();

  store.dispatch(updateVideoPlayerFromLinkExpand(fromLinkExpand));
  store.dispatch(updateFromPublicSharePage(fromPublicSharePage));
  store.dispatch(updateIsInlineEmbedOnLoom(!isMainVideoEmbedOnSharePage));
  store.dispatch(updateIsFromSlackDesktopApp(isFromSlackApp));
  store.dispatch(updateVideoPlayerParentLocation(parentLocation || fromUrl));
  store.dispatch(getUserCreator());
  window[USER] && store.dispatch(fetchWorkspaces());
  store.dispatch(initVideoCreator(window[VIDEO]));
  store.dispatch(getFeatureFlagCreator(SEND_HLS_PLAYBACK_STATS));

  injectCustomBrandColors();

  render(
    <Provider store={store}>
      <GraphQLProvider>
        <UrlQueryParamContextWrapper>
          <AnalyticsTrackPageWrapper pageName="embed-video">
            <EmbedVideo
              embedContext={{
                hideOwner,
                hideSpeedSelector,
                hideTitle,
                hideShare,
                hideEmbedTopBar,
              }}
            />
          </AnalyticsTrackPageWrapper>
        </UrlQueryParamContextWrapper>
      </GraphQLProvider>
    </Provider>,
    document.getElementById('container')
  );
}

try {
  window[VIDEO] && init();
} catch (error) {
  loggerx.fatal(
    error,
    {
      message: 'Embed Video page',
      hasVideo: Boolean(window?.[VIDEO]),
    },
    { team: 'create' }
  );
}
